import { inject, Injectable } from '@angular/core';
import {
  CustomGridStackWidget,
  GridWidgetConfigService,
  WidgetConfig,
} from './grid-widget-config.service';
import { DataLayerStore } from '../stores/data-layer/data-layer.state';
import { IndicatorDataStore } from '../stores/indicatpr-data/indicator-data.state';
import { DataLayer } from '../models/data-layer';
import { DataLayerTypeStore } from '../stores/data-layer-type/data-layer-type.state';
import { DataHelperService } from './data/data-helper.service';
import { GRID_TEMPLATES } from './atlas-template.presets';

@Injectable({
  providedIn: 'root',
})
export class AtlasTemplateService {
  readonly layerStore = inject(DataLayerStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly layerTypeStore = inject(DataLayerTypeStore);

  /** Named, indicator-free layout skeletons selectable from the Werkstatt empty state. */
  readonly templates = GRID_TEMPLATES;

  constructor(
    private gridWidgetService: GridWidgetConfigService,
    private dataHelperService: DataHelperService
  ) {}

  /**
   * Resolves a named template into fully-initialized but unconfigured widgets -
   * same shape as a manually added adder widget, just placed in the template's
   * arrangement. Unlike `generateGridFromTemplate()`, this doesn't require any
   * indicators to be selected first.
   *
   * @param {string} templateKey - The `GridTemplate.key` to resolve.
   * @return {CustomGridStackWidget[]} The resolved widgets, or `[]` if the key is unknown.
   */
  getEmptyLayoutFromTemplate(templateKey: string): CustomGridStackWidget[] {
    const template = this.templates.find(t => t.key === templateKey);
    if (!template) return [];
    return template.layout.map(widget =>
      this.gridWidgetService.getGridItemConfig(widget, {}, widget.type)
    );
  }

  /**
   * Generates a grid configuration based on the provided template and indicators.
   *
   * @param {string[]} indicators - An array of indicator strings used to fetch data and generate widgets.
   * @param {string} dataLayerType - The type of data layer used to organize and filter the data.
   * @param {Partial<CustomGridStackWidget>[]} [template] - An optional array of partial widget templates used as a base for the grid layout. If not provided, a default layout will be used.
   * @return {CustomGridStackWidget[]} An array of fully configured custom grid stack widgets.
   */
  generateGridFromTemplate(
    indicators: string[],
    dataLayerType: string,
    template?: Partial<CustomGridStackWidget>[]
  ): CustomGridStackWidget[] {
    if (!dataLayerType) {
      dataLayerType =
        this.dataHelperService.firstAvailableDataLayerTypeWithDataInIndicators(
          indicators
        )?._id;
    }
    if (!dataLayerType) return [];

    const dataByDataLayers = this.dataStore.getByDataLayerTypeAndIndicators(
      dataLayerType,
      indicators
    );

    let dataLayer: DataLayer | undefined = undefined;
    if (dataByDataLayers && Object.keys(dataByDataLayers).length > 0) {
      dataLayer = this.layerStore
        .getSortedByIds(Object.keys(dataByDataLayers))
        .at(0);
    }

    if (!dataLayer) {
      const type = this.layerTypeStore.getById(dataLayerType);
      dataLayer = this.layerStore.resolveDefaultByType(
        { _id: dataLayerType, defaultLayer: type?.defaultLayer },
        id => this.dataStore.dataLayerHasData(id)
      );
    }

    if (!dataLayer) return [];

    if (template) {
      return template as CustomGridStackWidget[];
    }

    const widgetPartial: Partial<WidgetConfig> = {
      indicator: indicators.at(0),
      indicators: [...indicators],
      dataLayer: dataLayer._id,
      year: String(dataLayer.year),
      dataLayerType: dataLayerType,
    };

    template = template || this.defaultLayout;
    return template.map(widget =>
      this.gridWidgetService.getGridItemConfig(
        widget,
        widgetPartial,
        widget.type
      )
    );
  }

  defaultLayout: Partial<CustomGridStackWidget>[] = [
    {
      id: 1,
      _id: 1,
      w: 8,
      h: 4,
      x: 0,
      y: 0,
      type: 'map',
    },
    {
      id: 12,
      _id: 12,
      w: 4,
      h: 2,
      x: 9,
      y: 0,
      type: 'info',
    },
    {
      id: 2,
      _id: 2,
      w: 4,
      h: 2,
      x: 8,
      y: 2,
      type: 'bar',
    },
    {
      id: 3,
      _id: 3,
      w: 8,
      h: 2,
      x: 0,
      y: 4,
      type: 'line',
    },
    {
      id: 4,
      _id: 4,
      w: 4,
      h: 2,
      x: 9,
      y: 4,
      type: 'table',
    },
  ];
}
